/**
 * Auto-rendered config form for an ingress adapter.
 *
 * Each adapter ships a JSON Schema (pydantic-generated) via
 * GET /ingress/types. We render one input per top-level property and
 * delegate validation to the backend (which re-runs the pydantic schema
 * on save).
 *
 * Mirrors the dynamic-form pattern in `NodeConfigPanel.tsx` but
 * decoupled from a workflow editor.
 */
import { CredentialPicker } from "@/components/credentials/CredentialPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CredentialType } from "@/lib/api-credentials";

interface JsonSchemaProperty {
  type?: string;
  description?: string;
  default?: unknown;
  enum?: unknown[];
  pattern?: string;
  minimum?: number;
  maximum?: number;
  anyOf?: Array<{ type?: string }>;
  items?: { type?: string };
  title?: string;
  /** Custom extension: when set, the field renders a CredentialPicker
   *  filtered to the listed credential types instead of a text input.
   *  Emitted by pydantic via ``json_schema_extra`` on the field. */
  "x-credential-type"?: string[];
}

export interface IngressConfigFormProps {
  schema: {
    properties?: Record<string, JsonSchemaProperty>;
    required?: string[];
  };
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

const MULTILINE_FIELDS = new Set(["query", "body"]);

function effectiveType(prop: JsonSchemaProperty): string {
  if (prop.enum) return "enum";
  if (prop.type) return prop.type;
  if (prop.anyOf) {
    const nonNull = prop.anyOf.find((a) => a.type && a.type !== "null");
    if (nonNull?.type) return nonNull.type;
  }
  return "string";
}

export function IngressConfigForm({ schema, value, onChange }: IngressConfigFormProps) {
  const properties = schema.properties ?? {};
  const required = new Set(schema.required ?? []);

  function set(field: string, v: unknown) {
    onChange({ ...value, [field]: v });
  }

  if (Object.keys(properties).length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        This adapter has no configurable options.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {Object.entries(properties).map(([name, prop]) => {
        const cur = value[name];
        const id = `ing-cfg-${name}`;
        const t = effectiveType(prop);
        const isRequired = required.has(name);
        const credentialTypes = prop["x-credential-type"] as
          | CredentialType[]
          | undefined;

        return (
          <div key={name} className="space-y-1.5">
            <Label htmlFor={id}>
              <span className="font-mono text-xs">{name}</span>
              {isRequired && <span className="text-destructive"> *</span>}
            </Label>

            {credentialTypes ? (
              // Credential reference field — render the typed picker
              // so the operator picks from existing credentials instead
              // of memorising UUIDs. The picker filters by the listed
              // types so e.g. webhook auth only sees api_key + hmac.
              <CredentialPicker
                id={id}
                value={(cur as string | null) ?? null}
                onChange={(v) => set(name, v)}
                type={credentialTypes}
              />
            ) : t === "enum" ? (
              <select
                id={id}
                className="block w-full border border-border bg-background px-2 py-1.5 text-xs"
                value={String(cur ?? "")}
                onChange={(e) => set(name, e.target.value)}
              >
                <option value="">—</option>
                {(prop.enum ?? []).map((opt) => (
                  <option key={String(opt)} value={String(opt)}>
                    {String(opt)}
                  </option>
                ))}
              </select>
            ) : t === "boolean" ? (
              <label className="flex items-center gap-2 text-xs">
                <input
                  id={id}
                  type="checkbox"
                  checked={!!cur}
                  onChange={(e) => set(name, e.target.checked)}
                />
                <span className="text-muted-foreground">
                  {prop.description ?? "Enable"}
                </span>
              </label>
            ) : t === "integer" || t === "number" ? (
              <Input
                id={id}
                type="number"
                value={cur !== undefined && cur !== null ? String(cur) : ""}
                min={prop.minimum}
                max={prop.maximum}
                onChange={(e) =>
                  set(
                    name,
                    e.target.value === "" ? undefined : Number(e.target.value),
                  )
                }
              />
            ) : t === "object" ? (
              <textarea
                id={id}
                rows={3}
                className="block w-full resize-y border border-border bg-background px-2 py-1.5 font-mono text-xs"
                value={cur ? JSON.stringify(cur, null, 2) : ""}
                onChange={(e) => {
                  try {
                    set(name, e.target.value ? JSON.parse(e.target.value) : {});
                  } catch {
                    // ignore — let user fix the JSON before save
                  }
                }}
                placeholder='{"key": "value"}'
              />
            ) : MULTILINE_FIELDS.has(name) ? (
              <textarea
                id={id}
                rows={4}
                className="block w-full resize-y border border-border bg-background px-2 py-1.5 font-mono text-xs"
                value={(cur as string) ?? ""}
                onChange={(e) => set(name, e.target.value)}
                placeholder={prop.description}
              />
            ) : (
              <Input
                id={id}
                value={(cur as string) ?? ""}
                onChange={(e) => set(name, e.target.value)}
                placeholder={prop.description}
              />
            )}
            {prop.description && !MULTILINE_FIELDS.has(name) && t !== "boolean" && (
              <p className="text-xs text-muted-foreground">{prop.description}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
